import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AuthenticationTabs from './components/AuthenticationTabs';
import LoginForm from './components/LoginForm';
import SignUpForm from './components/SignUpForm';
import SocialAuthSection from './components/SocialAuthSection';
import TrustSignals from './components/TrustSignals';
import Icon from '../../components/AppIcon';

const UserAuthentication = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user is already authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    
    if (isAuthenticated) {
      // Redirect authenticated users
      const intendedPath = location?.state?.from || '/prompt-generator';
      navigate(intendedPath, { replace: true });
      return;
    }

    // Set initial tab based on URL params or default to login
    const urlParams = new URLSearchParams(location.search);
    const mode = urlParams?.get('mode');
    if (mode === 'signup') {
      setActiveTab('signup');
    }

    setIsLoading(false);
  }, [navigate, location]);

  const handleAuthSuccess = () => {
    // This will be called by child components on successful authentication
    const intendedPath = location?.state?.from || '/prompt-generator';
    navigate(intendedPath, { replace: true });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Update URL without navigation
    const url = new URL(window.location);
    if (tab === 'signup') {
      url?.searchParams?.set('mode', 'signup');
    } else {
      url?.searchParams?.delete('mode');
    }
    window.history?.replaceState({}, '', url);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <Icon name="Zap" size={24} color="var(--color-primary)" />
          </div>
          <p className="text-sm text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="min-h-screen flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            {/* Header Section */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name="Zap" size={32} color="var(--color-primary)" />
              </div>
              
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                {activeTab === 'login' ? 'Welcome Back' : 'Get Started'}
              </h1>
              
              <p className="text-text-secondary">
                {activeTab === 'login' ?'Sign in to access your saved prompts and premium features' :'Create your account to start generating AI-powered prompts'
                }
              </p>
            </div>

            {/* Authentication Form */}
            <div className="bg-surface border border-border rounded-xl shadow-soft p-8">
              <AuthenticationTabs 
                activeTab={activeTab} 
                onTabChange={handleTabChange} 
              />

              {activeTab === 'login' ? (
                <LoginForm onSuccess={handleAuthSuccess} />
              ) : (
                <SignUpForm onSuccess={handleAuthSuccess} />
              )}

              <div className="mt-8">
                <SocialAuthSection onSuccess={handleAuthSuccess} />
              </div>
            </div>

            {/* Additional Information */}
            <div className="mt-8 text-center">
              <p className="text-sm text-text-secondary">
                {activeTab === 'login' ? (
                  <>
                    Don't have an account?{' '}
                    <button
                      onClick={() => handleTabChange('signup')}
                      className="text-primary hover:text-primary/80 font-medium transition-smooth"
                    >
                      Sign up for free
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      onClick={() => handleTabChange('login')}
                      className="text-primary hover:text-primary/80 font-medium transition-smooth"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Trust Signals Section */}
        <section className="bg-muted/30 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <TrustSignals />
          </div>
        </section>
      </main>
    </div>
  );
};

export default UserAuthentication;